import React, { useState, useContext, useEffect, useMemo, forwardRef, useImperativeHandle } from "react";
import { TextInput } from "../Shared/Inputs/TextInput";
import { FileInput } from "../Shared/Inputs/FileInput";
import { JobPostingModelValidation, VacancyPostingModel } from "../../models/VacancyPostingModel";
import { useMediaQuery } from 'react-responsive';
import { Switch } from "../Shared/Buttons/Switch";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedRecruiter } from "../../models/LoggedRecruiter";
import { ReactComponent as CloseCircle } from "../../assets/svg/close-circle.svg";
import { ReactComponent as RedCloseCircle } from "../../assets/svg/close-circle-red.svg";
import { ReactComponent as Plus } from '../../assets/svg/plus.svg';
import { TextAreaInput } from "../Shared/Inputs/TextAreaInput";
import { QuillEditor } from "../Shared/QuillEditor";
import { Sector } from "../../models/Sector";
import { EducationLevel } from "../../models/EducationLevel";
import { ExperienceLevel } from "../../models/ExperienceLevel";
import { useAlert } from "../../hooks/useAlert";
import { getJobDescriptionImageURL, validateEmails, validateImageFile } from "../../helpers/Common";
import { PredefinedBenefit } from "../../models/PredefinedBenefit";
import { PopUpWindow } from '../Shared/PopUpWindow';
import { GeoLocation } from "../../models/GeoLocation";
import { SummaryOfCompletionModel } from "../../models/SummaryOfCompletionModel";
import { SelectorModel } from "../../models/SelectorModel";
import { Selector } from "../Shared/Selector";
import { getRecruiterQuotaDetails } from "../../services/RecruiterService";
import { RecruiterQuotaDetails } from "../../models/RecruiterQuotaDetails";
import useErrorPage from "../../hooks/useErrorPage";

interface Props {  
    styles: any;
    jobId?: number;
    initialImageFile?: File | null;
    onUpdateSummary: (model?: Partial<SummaryOfCompletionModel>) => void;
    onTabChange: (tab: string) => void;
    onLoad: () => Promise<VacancyPostingModel>;
    onSave: (model: VacancyPostingModel, image?: File | null) => void;
}

export interface BasicDetailsEditHandle {
    saveTab: () => Promise<{ data: VacancyPostingModel; file?: File | null } | null>;
}

const initialVacancyDetails: VacancyPostingModel = {
    job: {
        jobId: 0,
        organizationId: 0,
        recruiterId: 0,
        jobTitle: "",
        overview: "",
        internalRef: "",
        isPreApplicationFormEnabled: false,
        confirmationPending: true,
        isTemp: true,
        notifyMail: "",
        minimumSalary: 0,
        expiryDateOnWebsite: new Date(),
        maximumSalary: 0,
        jobDescription: "",
        benefits: "",
        isArchiveByLimit: false,
        published: false,
        cvLimit: 0,
        sectors: [],
        locations: [],
        jobType:{
            jobTypeName: "",
            jobTypeId: 0
        },
        createdDate: new Date(),
        jobBenefits:[]
    },
    recruiterName: "",
    sectors: [],
    locations: [],
    careerLevels: [],
    predefinedExperienceLevels: [],
    predefinedEducationLevels: [],
    jobTypes: [],
    jobBenefits: [],
    benefitsList: [],
    recruiterList: [],
    jobEducationLevel: "",
    jobExperienceLevel: "",
    jobCareerLevel: "",
    careerLevelId: 0,
    attachment: "Choose image"
}

const initialValidation: JobPostingModelValidation = {
    jobTitle: {},
    jobType: {},
    sectors: {},
    locations: {},
    notifyMail: {},
    cvLimit: {},
    minimumSalary: {},
    maximumSalary: {}
}

export const BasicDetailsEdit= forwardRef<BasicDetailsEditHandle, Props>((props, ref) => {
    const styles = props.styles;
    const authContext = useContext(AuthContext);
    const recruiter: LoggedRecruiter = authContext.getLoggedRecruiter();
    const [expandItem, setExpandItem] = useState<string>();
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [hoveredSectorId, setHoveredSectorId] = useState<number>(0);
    const [sectorSearchQuery, setSectorSearchQuery] = useState<string>("");
    const [locations, setLocations] = useState<GeoLocation[]>([]);
    const [hoveredLocationId, setHoveredLocationId] = useState<number>(0);
    const [locationSearchQuery, setLocationSearchQuery] = useState<string>("");   
    const [customBenefit, setCustomBenefit] = useState("");
    const [benefits, setBenefits] = useState<PredefinedBenefit[]>([]);
    const [hoveredBenefitId, setHoveredBenefitId] = useState<number>(0);    
    const [jobTypes, setJobTypes] = useState<SelectorModel[]>([]);
    const [careerLevels, setCareerLevels] = useState<SelectorModel[]>([]);
    const [recruiterList, setRecruiterList] = useState<SelectorModel[]>([]);
    const [quotaDetails, setQuotaDetails] = useState<RecruiterQuotaDetails>(); 
    const [educationLevels, setEducationLevels] = useState<EducationLevel[]>([]);
    const [experienceLevels, setExperienceLevels] = useState<ExperienceLevel[]>([]);
    const [jobDescriptionImage, setJobDescriptionImage] = useState<File | null>(props.initialImageFile ?? null);
    const [jobDescriptionImageUrl, setJobDescriptionImageUrl] = useState<string | null>(null);
    const [viewJobImage, setViewJobImage] = useState<boolean>(false);
    const [vacancyPostingModel, setVacancyPostingModel] = useState<VacancyPostingModel>(initialVacancyDetails);
    const [validation, setValidation] = useState<JobPostingModelValidation>(initialValidation);    
    const { Alert, openAlert } = useAlert();
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [recruiterUpdateEnable, setRecruiterUpdateEnable] = useState<boolean>(false);
    const [toErrorPage] = useErrorPage();
    
    useEffect(() => { 
        getBasicDetails();
        getQuotaDetails();
        window.scrollTo({ top: 0, behavior: 'smooth' });    
    }, []);

    const getBasicDetails = async () => {
        const jobDetails = await props.onLoad();
        setVacancyPostingModel(jobDetails);
        selectSectorList(jobDetails.sectors, jobDetails.job.sectors);
        selectLocationList(jobDetails.locations, jobDetails.job.locations);
        selectBenefitList(jobDetails.benefitsList, jobDetails.job.benefits)
        setJobTypes(jobDetails.jobTypes);
        setCareerLevels(jobDetails.careerLevels);
        setEducationLevels(jobDetails.predefinedEducationLevels);
        setExperienceLevels(jobDetails.predefinedExperienceLevels);
        setRecruiterList(jobDetails.recruiterList);
        if(jobDetails.job.jobId > 0) {
            const imageUrl = getJobDescriptionImageURL(jobDetails.job.jobId);
            const img = new Image();
            img.src = imageUrl;
            img.onload = () => {
                setJobDescriptionImageUrl(imageUrl);
            };
            img.onerror = () => {
                setJobDescriptionImageUrl(null);
            };
        }
        if(recruiter.isSystemAdmin || recruiter.isOrganizationAdmin || recruiter.isMasterAccess) {
            setRecruiterUpdateEnable(true);
        }
    }

    async function getQuotaDetails() {                
        await getRecruiterQuotaDetails(recruiter!.recruiterId, recruiter!.organizationId, recruiter!.token)
        .then((recruiterQuotaDetails: RecruiterQuotaDetails) => {            
            setQuotaDetails(recruiterQuotaDetails);               
        })
        .catch((error) => {
            toErrorPage(error);
        });    
    }

    const filteredSectors = useMemo(() => {
        const trimmed = sectorSearchQuery.trim();
    
        if (!trimmed) return sectors;
    
        const query = trimmed.toLowerCase();
    
        return sectors.filter((sector) => {
            const sectorName = (sector.sectorName ?? '').toLowerCase();      
            return sectorName.includes(query);
        });
    }, [sectorSearchQuery, sectors]);

    const filteredLocation = useMemo(() => {
        const trimmed = locationSearchQuery.trim();
    
        if (!trimmed) return locations;
    
        const query = trimmed.toLowerCase();
    
        return locations.filter((location) => {
            const locationName = (location.locationName ?? '').toLowerCase();      
            return locationName.includes(query);
        });
    }, [locationSearchQuery, locations]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        const name = event.target.name;

        const numericFields = ["minimumSalary", "maximumSalary", "cvLimit"];
        let parsedValue: any;

        if (name === "expiryDateOnWebsite") {
            parsedValue = new Date(value);
        }
        else if (numericFields.includes(name)) {
            parsedValue = value === "" ? 0 : Number(value);
        }
        else {
            parsedValue = value;
        }

        setVacancyPostingModel((prevState) => {
            if(name in prevState.job) {
                return {
                    ...prevState,
                    job: {
                        ...prevState.job,
                        [name]: parsedValue          
                    }
                };
            }
            return {
                ...prevState,
                [name]: value
            };
        });
        if(validation[name] && validation[name]!.invalid) {
            validateInput(name, value);
        }
    }

    const handleTextAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = event.target.name;
        const value = event.target.value;

        setVacancyPostingModel(prev => ({
            ...prev,
            job: {
                ...prev.job,
                [name]: value,
            }
        }));
    }

    const handleFileChange = (selectedFiles: FileList) => {
        if(selectedFiles.length > 0) {
            let validation = validateImageFile(selectedFiles[0]);
            if(validation.valid) {
                setJobDescriptionImage(selectedFiles[0]);            
                setVacancyPostingModel((values: any) => ({ ...values, ["attachment"]: selectedFiles[0].name })); 
            }
            else {
                openAlert("error", validation.invalidMessage!)
            }
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;

        validateInput(name, value);    
    }

    const selectSectorList = (sectors: Sector[], jobSectors: Sector[]) => {
        const selectedIds = new Set<number>(
            (jobSectors ?? []).map((s) => s.sectorId)
        );
        
        setSectors(
            sectors.map(sector => ({
                ...sector,
                isSelected: selectedIds.has(sector.sectorId)
            }))
        );
    }

    const toggleSector = (sectorId: number) => { 
        const updatedSectors = sectors.map(sector => 
            sector.sectorId === sectorId 
                ? { ...sector, isSelected: !sector.isSelected }
                : sector);

        setSectors(updatedSectors);
        
        const hasSelectedSectors = updatedSectors.some(sector => sector.isSelected);
        setValidation((values: any) => ({
            ...values, 
            sectors: {
                valid: hasSelectedSectors, 
                invalid: !hasSelectedSectors
            }
        }));
        setSectorSearchQuery("");
        setExpandItem(undefined);
    } 

    const selectLocationList = (locations: GeoLocation[], jobLocation: GeoLocation[]) => {
        const selectedIds = new Set<number>(
            (jobLocation ?? []).map(loc => loc.locationId)
        );
        
        setLocations(
            locations.map(location => ({
                ...location,
                isSelected: selectedIds.has(location.locationId)
            }))
        );
    }

    const toggleLocation = (locationId: number) => { 
        const updatedLocations = locations.map(location => 
            location.locationId === locationId 
                ? { ...location, isSelected: !location.isSelected }
                : location);

        setLocations(updatedLocations);

        const hasSelectedLocations = updatedLocations.some(location => location.isSelected);
        setValidation((values: any) => ({
            ...values, 
            locations: {
                valid: hasSelectedLocations, 
                invalid: !hasSelectedLocations
            }
        }));
        setExpandItem(undefined);       
        setLocationSearchQuery("");     
    } 

    const selectBenefitList = (predefinedList: PredefinedBenefit[], jobBenefits: string) => {
        const selectedBenefitNames = (jobBenefits ?? "")
            .split(",")
            .map((b) => b.trim())
            .filter((b) => b.length > 0);

        const nameSet = new Set(selectedBenefitNames);

        const updatedPredefined = predefinedList.map((benefit) => ({
            ...benefit,
            isSelected: nameSet.has(benefit.benefit)
        }));

        const predefinedNames = new Set(predefinedList.map(p => p.benefit));
        
        const customBenefits = selectedBenefitNames
            .filter(name => !predefinedNames.has(name))
            .map(name => ({
                benefitId: Math.random(), 
                benefit: name,
                isSelected: true,
                isCustom: true 
            }));

        setBenefits([...updatedPredefined, ...customBenefits]);
    }

    const toggleBenefit = (benefitId: number) => {   
        setBenefits((prevBenefit) =>
            prevBenefit.map(benefit => 
                benefit.benefitId === benefitId 
                    ? { ...benefit, isSelected: !benefit.isSelected}
                    : benefit
        ));
        setExpandItem(undefined);
    }

    const addCustomBenefit = (benefit: PredefinedBenefit) => {
        setBenefits((prevBenefits) => {return [...prevBenefits, benefit]});
    }

    const addExperience = (experience: ExperienceLevel) => {
        setVacancyPostingModel((prevState) => ({ ...prevState, ["jobExperienceLevel"]: experience.experienceLevel}));
        setExpandItem(undefined); 
    }

    const addEducationlevel = (education: EducationLevel) => {
        setVacancyPostingModel((prevState) => ({ ...prevState, ["jobEducationLevel"]: education.educationLevel }));
        setExpandItem(undefined);
    }

    const formatDateForInput = (date?: Date | string | null): string => {
        if(!date) return "";
        
        if(typeof date === 'string') {
            return date.split('T')[0];
        }
        
        const dateObj = date as Date;
        if(isNaN(dateObj.getTime())) return "";
        
        return dateObj.toISOString().split("T")[0];
    }

    const handleSwitchChange = (name: 'isPreApplicationFormEnabled' | 'isArchiveByLimit') => {
        const newValue = !vacancyPostingModel.job[name];

        setVacancyPostingModel(prev => ({
            ...prev,
            job: {
                ...prev.job,
                [name]: newValue
            }
        }));

        if (name === 'isArchiveByLimit' && !vacancyPostingModel.job.isArchiveByLimit) {
            setVacancyPostingModel(prev => ({
                ...prev,
                job: {
                    ...prev.job,
                    cvLimit: 0
                }
            }));
        }

        if (name === 'isPreApplicationFormEnabled') {
            props.onUpdateSummary({
                cvLessQuestion: newValue,
                vacancyQuestions: !newValue
            });
        }
    };

    const handleSelectItems = (selector: string, selectedItem: SelectorModel) => {
        if (selector === "jobType") {
            setVacancyPostingModel(prev => ({
                ...prev,
                job: {
                    ...prev.job,
                    jobType: {
                        jobTypeId: selectedItem.id ?? 0,
                        jobTypeName: selectedItem.value
                    }
                }
            }));
        } else if (selector === "recruireName") {
            setVacancyPostingModel(prev => ({
                ...prev,
                job: {
                    ...prev.job,
                    recruiterId: selectedItem.id ?? 0
                }, 
                recruiterName: selectedItem.value
            }));
        } else if (selector === "careerLevel") {
            setVacancyPostingModel(prev => ({
                ...prev,
               careerLevelId: selectedItem.id ?? 0,
               jobCareerLevel: selectedItem.value
            }));
        } else {
            setVacancyPostingModel(prev => ({
                ...prev,
                job: {
                    ...prev.job,
                    [selector]: selectedItem.value
                }
            }));
        }

        validateInput(selector, selectedItem.value);
    };

    const handleJobDescriptionChange = (content: string) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        const textContent = (tempDiv.textContent || '').trim();

        const rowContent = textContent.length === 0 ? '' : content;
        
        setVacancyPostingModel(prev => ({
            ...prev,
            job: {
                ...prev.job,
                jobDescription: rowContent
            }
        }));
        tempDiv.remove();
    };

    const validateInput = (name: string, value?: string) => {    
        if(name === "jobTitle") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Job Title is required"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "cvLimit" && vacancyPostingModel.job.isArchiveByLimit) {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "CV Limit is required"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "notifyMail") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            } 
            else if(!validateEmails(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "jobType") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Job Type is required"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }          
        }
        if(name === "maximumSalary") {                
            if(Number(value) < 0) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Maximum salary can not be a negative value."}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "minimumSalary") {            
            if(Number(value) < 0) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Minimum salary can not be a negative value."}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
    }

    const validateForm = (jobDetail: VacancyPostingModel): number => {
        let formValidation: JobPostingModelValidation = {
            jobTitle: {valid: true},
            jobType: {valid: true},
            sectors: {valid: true},
            locations: {valid: true},
            notifyMail: {valid: true},
            cvLimit: {valid: true},
            maximumSalary: {valid: true},
            minimumSalary: {valid: true}
        }  
        
        if(jobDetail.job.jobTitle === "" || jobDetail.job.jobTitle === null) {   
            formValidation.jobTitle = {valid: false, invalid: true, invalidMessage: "Job Title is required"};
        } 
        
        if(jobDetail.job.jobType?.jobTypeName === "" || jobDetail.job.jobType?.jobTypeName === undefined) {
            formValidation.jobType = {valid: false, invalid: true};
        } 

        if(!jobDetail.job.sectors || jobDetail.job.sectors.length === 0) {
            formValidation.sectors = { valid: false, invalid: true };
        }

        if(!jobDetail.job.locations || jobDetail.job.locations.length === 0) {
            formValidation.locations = { valid: false, invalid: true };
        }
          
        if(jobDetail.job.isArchiveByLimit && jobDetail.job.cvLimit <= 0) {
            formValidation.cvLimit = { valid: false, invalid: true, invalidMessage: "CV Limit is required"};
        }

        if(jobDetail.job.maximumSalary < 0) {      
            formValidation.maximumSalary = { valid: false, invalid: true, invalidMessage: "Maximum salary can not be a negative value."};
        }

        if(jobDetail.job.minimumSalary < 0) {
            formValidation.minimumSalary = { valid: false, invalid: true, invalidMessage: "Minimum salary can not be a negative value."};
        }
        
        if(jobDetail.job.notifyMail === "") {
            formValidation.notifyMail = {valid: true, invalid: false};
        } 
        else if(!validateEmails(jobDetail.job.notifyMail)) {
            formValidation.notifyMail = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        } 
        
        setValidation(formValidation);       
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount; 
    }

    const handleViewJobImage = () => {
        setViewJobImage(true);
    }

    const handleRemoveJobImage = () => {
        setVacancyPostingModel((prevState) => ({ ...prevState, ["removeJobDescriptionImage"]: true }));
        setJobDescriptionImageUrl("");
        openAlert("success", "The description image was removed successfully.");      
    }

    const handleSectorSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSectorSearchQuery(value);
        setExpandItem("sectors");
    }

    const handleSectorKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if(e.key === 'Enter' && filteredSectors.length > 0) {
            e.preventDefault(); 
            toggleSector(filteredSectors[0].sectorId);
            setSectorSearchQuery(""); 
        }
    }

    const handleLocationSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setLocationSearchQuery(value); 
        setExpandItem("location");
    }

    const handleLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if(e.key === 'Enter' && filteredLocation.length > 0) {
            e.preventDefault(); 
            toggleLocation(filteredLocation[0].locationId);
            setLocationSearchQuery(""); 
        }
    }

    const handleAddCustomBenefit = () => {
        const trimmed = customBenefit.trim();
        if(trimmed === "") return;
        
        const newBenefit: PredefinedBenefit = {
            benefitId: Date.now(), 
            benefit: trimmed,
            isSelected: true
        };
        addCustomBenefit(newBenefit);
        setCustomBenefit("");   
        setExpandItem(undefined);
    }

    const handleCustomBenefitKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if(e.key === "Enter") {
            e.preventDefault();
            handleAddCustomBenefit();
        }
    }

    const buildUpdatedJobDetail = (): VacancyPostingModel => {
        const selectedSectors = sectors.filter(sector => sector.isSelected);
        const selectedLocations = locations.filter(location => location.isSelected);
        const selectedBenefits = benefits.filter(benefit => benefit.isSelected).map(benefit => benefit.benefit).join(",");
        return {
            ...vacancyPostingModel,
            job: {
                ...vacancyPostingModel.job,
                sectors: selectedSectors,        
                locations: selectedLocations,    
                benefits: selectedBenefits,
                isTemp: false,
                archived: false
            }
        };
    };

    const saveTab = async (): Promise<{ data: VacancyPostingModel; file?: File | null } | null> => {
        const updatedJobDetail = buildUpdatedJobDetail();
        const validInputCount = validateForm(updatedJobDetail);

        if (validInputCount !== 8) {
            openAlert("error", "Some mandatory fields are invalid.");
            return null; 
        }

        props.onSave(updatedJobDetail, jobDescriptionImage);

        const summaryUpdates: Partial<SummaryOfCompletionModel> = {
            basicInfo: true,
            required: true,
            salary: (updatedJobDetail.job.minimumSalary > 0) || (updatedJobDetail.job.maximumSalary > 0),
            description: !!updatedJobDetail.job.jobDescription?.trim()
        };
        if (updatedJobDetail.job.isPreApplicationFormEnabled) {
            summaryUpdates.cvLessQuestion = true;
            summaryUpdates.vacancyQuestions = false;
            props.onUpdateSummary(summaryUpdates);
            
        } else {
            summaryUpdates.vacancyQuestions = true;
            summaryUpdates.cvLessQuestion = false;
            props.onUpdateSummary(summaryUpdates);
        }

        return { data: updatedJobDetail, file: jobDescriptionImage };
    };

    useImperativeHandle(ref, () => ({ saveTab }));

    return(
        <div style={styles.mainWrappre}>            
            {isMobile ? <div style={styles.subTopic}>{"Basic Info"}</div> :
            <div style={styles.subTopicWrapper}>
                <div style={styles.subTopicWrapper.topic}>{"Basic Details"}</div>
            </div>}
            <form style={styles.form}>
                <div style={styles.inputWrapper}>
                    <label style={styles.label}>{"Job Title *"}</label>
                    <TextInput
                        inputType={"text"}
                        name={"jobTitle"}
                        value={vacancyPostingModel.job?.jobTitle || ""}
                        styles={styles.textInput}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.jobTitle}     
                        disabled={true}                      
                    />    
                </div>
                <div style={styles.inputWrapper}>
                    <Selector
                        label={"Recruiter *"} 
                        placeHolder={"Select One"}
                        items={recruiterList}
                        name={"recruireName"}
                        value={vacancyPostingModel.recruiterName}
                        onSelectItem={handleSelectItems}
                        styles={styles.selector}
                        disabled={!recruiterUpdateEnable}
                    />
                </div>
                <div style={styles.groupWrapper}>
                    <div style={{...styles.inputWrapper, ...styles.rightMargin}}>
                        <Selector
                            label={"Job Type *"} 
                            placeHolder={"Select One"}
                            items={jobTypes}
                            name= {"jobType"}
                            value={vacancyPostingModel?.job?.jobType?.jobTypeName || ""}
                            onSelectItem={handleSelectItems}
                            validation={validation.jobType}
                            styles={styles.selector}
                        />
                    </div>
                    <div style={{...styles.inputWrapper, ...styles.leftMargin}}>
                        <Selector
                            label={"Job Level "} 
                            placeHolder={"Select One"}
                            items={careerLevels}
                            name= {"careerLevel"}
                            value={vacancyPostingModel.jobCareerLevel}
                            onSelectItem={handleSelectItems}
                            styles={styles.selector}
                        />
                    </div>
                </div>
                <div 
                    style={styles.inputWrapper}
                    onMouseEnter={() => setExpandItem("sectors")}
                    onMouseLeave={() => setExpandItem(undefined)} 
                    onClick={() => setExpandItem(expandItem === "sectors" ? undefined : "sectors")} 
                >
                    <label style={styles.label}>{"Sectors *"}</label>
                    <div 
                        style={{
                            ...styles.searchContainer, 
                            ...((expandItem === 'sectors') && styles.searchContainer.hover), 
                            ...(validation?.sectors.valid && styles.valid), 
                            ...(validation?.sectors.invalid && styles.invalid) 
                        }}
                    >
                        {sectors.filter((sector) => sector.isSelected).map((Sector) =>
                            <div 
                                key={Sector.sectorId}
                                style={styles.selectorWrapper}
                                onMouseEnter={() => setHoveredSectorId(Sector.sectorId)}
                                onMouseLeave={() => setHoveredSectorId(0)}
                            >
                                {Sector.sectorName}
                                {hoveredSectorId === Sector.sectorId && <CloseCircle style={styles.closeIcon} onClick={() => toggleSector(Sector.sectorId)} />}
                            </div>
                        )}
                        <input
                            type="text"
                            value={sectorSearchQuery}
                            onChange={handleSectorSearchChange}
                            onKeyDown={handleSectorKeyDown}
                            style={{
                                ...styles.searchInput, 
                                ...(validation?.sectors.valid && styles.searchInput.valid), 
                                ...(validation?.sectors.invalid && styles.searchInput.invalid)
                            }}
                        />                                     
                    </div>
                    {(expandItem === 'sectors') && <div style={styles.benifitHoverWrapper}>
                        <div style={styles.levelScrollArea}>
                            {filteredSectors.filter((sector) => !sector.isSelected).map((sector, index) => 
                                <React.Fragment key={sector.sectorId}>
                                    <div 
                                        style={{ 
                                            ...styles.levelDropDownItem, 
                                            ...(hoveredIndex === index && styles.levelDropDownItem.hover)
                                        }}
                                        onClick={() => toggleSector(sector.sectorId)} 
                                        onMouseEnter={() => setHoveredIndex(index)} 
                                        onMouseLeave={() => setHoveredIndex(-1)}
                                    >
                                        {sector.sectorName}
                                    </div>
                                    {index < filteredSectors.filter((s) => !s.isSelected).length -1 && 
                                    <hr 
                                        style={{ 
                                            ...styles.benifitbottomBorder, 
                                            ...((hoveredIndex === index) && styles.benifitbottomBorder.hover) 
                                        }} 
                                    />}   
                                </React.Fragment>
                            )}
                        </div>
                    </div>}
                </div>
                <div 
                    style={styles.inputWrapper}
                    onMouseEnter={() => setExpandItem("location")}
                    onMouseLeave={() => setExpandItem(undefined)}
                    onClick={() => setExpandItem(expandItem === "location" ? undefined : "location")}
                >
                    <label style={styles.label}>{"Locations *"}</label>
                    <div 
                        style={{
                            ...styles.searchContainer, 
                            ...((expandItem === "location") && styles.searchContainer.hover), 
                            ...(validation?.locations.valid && styles.valid), 
                            ...(validation?.locations.invalid && styles.invalid) 
                        }}
                    >
                        {locations.filter((location) => location.isSelected).map((location) =>
                            <div 
                                key={location.locationId}
                                style={styles.selectorWrapper}
                                onMouseEnter={() => setHoveredLocationId(location.locationId)}
                                onMouseLeave={() => setHoveredLocationId(0)}
                            >
                                {location.locationName}
                                {hoveredLocationId === location.locationId && <CloseCircle style={styles.closeIcon} onClick={() => toggleLocation(location.locationId)} />}
                            </div>
                        )}
                        <input
                            type="text"
                            value={locationSearchQuery}
                            onChange={handleLocationSearchChange}
                            onKeyDown={handleLocationKeyDown}
                            style={{
                                ...styles.searchInput, 
                                ...(validation?.locations.valid && styles.searchInput.valid), 
                                ...(validation?.locations.invalid && styles.searchInput.invalid)
                            }}
                        />                                     
                    </div>
                    {(expandItem === "location") && <div style={styles.benifitHoverWrapper}>
                        <div style={styles.levelScrollArea}>
                            {filteredLocation.filter((location) => !location.isSelected).map((location, index) => 
                                <React.Fragment key={index}>
                                    <div 
                                        style={{ 
                                            ...styles.levelDropDownItem, 
                                            ...(hoveredIndex === index && styles.levelDropDownItem.hover) 
                                        }}
                                        onClick={() => toggleLocation(location.locationId)} 
                                        onMouseEnter={() => setHoveredIndex(index)} 
                                        onMouseLeave={() => setHoveredIndex(-1)}
                                    >
                                        {location.locationName}
                                    </div>
                                    {index < filteredLocation.filter((s) => !s.isSelected).length -1 && 
                                    <hr 
                                        style={{ 
                                            ...styles.benifitbottomBorder, 
                                            ...((hoveredIndex === index) && styles.benifitbottomBorder.hover) 
                                        }} 
                                    />} 
                                </React.Fragment>
                            )}
                        </div>
                    </div>}
                </div>
                <div style={{...styles.groupWrapper, ...styles.topMargin}}>
                    <div style={
                            quotaDetails?.quotaType === "CV Based" 
                                ? styles.cvBaseWrapper 
                                : {...styles.cvBaseWrapper, ...styles.cvBaseWrapper.monthly}
                        }
                    >
                        {"CV Less Job"}
                        <Switch 
                            styles={styles.tabSwitch} 
                            checked={vacancyPostingModel.job?.isPreApplicationFormEnabled} 
                            handleClick={() => handleSwitchChange("isPreApplicationFormEnabled")}
                        />
                    </div>
                    {quotaDetails?.quotaType === "CV Based" && (
                    <div style={styles.cvLimitWrapper}>     
                        <label style={styles.label}>{"CV Limit"}</label>  
                        <TextInput
                            inputType={"number"}
                            name={"cvLimit"}
                            value={vacancyPostingModel.job?.cvLimit === 0 ? "" : vacancyPostingModel.job?.cvLimit}
                            min={0}
                            styles={{...styles.textInput, ...styles.cvLimit}}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            disabled={true}                           
                            validation={validation.cvLimit}                           
                        /> 
                    </div>
                    )}                      
                </div>
                <div style={styles.groupWrapper}>
                    <div 
                        style={{
                            ...styles.inputWrapper, 
                            ...styles.rightMargin
                        }} 
                        onMouseEnter={() => setExpandItem("experienceLevel")}
                        onMouseLeave={() => setExpandItem(undefined)} 
                    >
                        <label style={styles.label}>{"Experience Level"}</label>
                        <TextInput
                            inputType={"text"}
                            name={"jobExperienceLevel"}
                            value={vacancyPostingModel.jobExperienceLevel}
                            styles={styles.textInput}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}                                            
                        />
                        {(expandItem === "experienceLevel") && <div style={styles.levelHoverWrapper}>
                            <div style={styles.levelScrollArea}>
                                {experienceLevels.map((experience, index) => 
                                    <React.Fragment key={index}>
                                        <div 
                                            style={{ 
                                                ...styles.levelDropDownItem, 
                                                ...(hoveredIndex === index && styles.levelDropDownItem.hover) 
                                            }} 
                                            onClick={() => addExperience(experience)} 
                                            onMouseEnter={() => setHoveredIndex(index)} 
                                            onMouseLeave={() => setHoveredIndex(-1)}
                                        >
                                            {experience.experienceLevel}
                                        </div>
                                        {index < experienceLevels.length -1 && 
                                        <hr 
                                            style={{
                                                ...styles.levelBottomBorder, 
                                                ...((hoveredIndex === index)  && styles.levelBottomBorder.hover) 
                                            }} 
                                        />}
                                    </React.Fragment>
                                )}
                            </div>
                        </div>}   
                    </div>
                    <div 
                        style={{
                            ...styles.inputWrapper,
                            ...styles.leftMargin
                        }}
                        onMouseEnter={() => setExpandItem("educationLevel")}
                        onMouseLeave={() => setExpandItem(undefined)}  
                    >
                        <label style={styles.label}>{"Education Level"}</label>
                        <TextInput
                            inputType={"text"}
                            name={"jobEducationLevel"}
                            value={vacancyPostingModel.jobEducationLevel}
                            styles={styles.textInput}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}                                            
                        />
                        {(expandItem === "educationLevel") && <div style={styles.levelHoverWrapper}>
                            <div style={styles.levelScrollArea}>
                                {educationLevels.map((education, index) => 
                                    <React.Fragment key={index}>
                                        <div 
                                            style={{ 
                                                ...styles.levelDropDownItem, 
                                                ...(hoveredIndex === index && styles.levelDropDownItem.hover) 
                                            }}
                                            onClick={() => addEducationlevel(education)} 
                                            onMouseEnter={() => setHoveredIndex(index)} 
                                            onMouseLeave={() => setHoveredIndex(-1)}
                                        >
                                            {education.educationLevel}
                                        </div>
                                        {index < educationLevels.length - 1 &&  
                                        <hr 
                                            style={{ 
                                                ...styles.levelBottomBorder, 
                                                ...((hoveredIndex === index ) && styles.levelBottomBorder.hover) 
                                            }} 
                                        />}                                
                                    </React.Fragment>
                                )}
                            </div>
                        </div>}   
                    </div>
                </div>
                <div style={styles.groupWrapper}>
                    <div style={{...styles.inputWrapper, ...styles.rightMargin}}>
                        <label style={styles.label}>{"Notify Email"}</label>
                        <TextInput
                            inputType={"text"}
                            name={"notifyMail"}
                            value={vacancyPostingModel.job?.notifyMail}
                            styles={styles.textInput}
                            placeHolder={"Notify Mail"}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.notifyMail}
                            disabled={true}                          
                        />    
                    </div>
                    <div style={{...styles.dateWrapper, ...styles.leftMargin}}>
                        <label style={styles.label}>{"Expiry Date on Website"}</label>
                        <input
                            type={"date"}
                            min={new Date().toISOString().split("T")[0]}
                            style={styles.dateInput}
                            name={"expiryDateOnWebsite"}
                            placeholder={"DD/MM/YYYY"}
                            onChange={handleInputChange}
                            value={formatDateForInput(vacancyPostingModel.job?.expiryDateOnWebsite)}
                            data-date-format="MM-DD-YYYY"
                            onKeyDown={(e) => {
                                if (e.key !== 'Tab') {
                                    e.preventDefault();
                                }
                            }}
                            onPaste={(e) => e.preventDefault()}
                            onCut={(e) => e.preventDefault()}
                            onDrop={(e) => e.preventDefault()}
                            onInput={(e) => e.preventDefault()}
                            autoComplete="off"
                        />
                    </div>
                </div>
                <hr style={styles.line1}/>
                <label style={styles.label}>{"Salary Band"}</label>
                <div style={styles.groupWrapper}>
                    <div style={{...styles.inputWrapper, ...styles.rightMargin}}>
                       <TextInput
                            inputType={"number"}
                            name={"minimumSalary"}
                            value={vacancyPostingModel.job?.minimumSalary === 0 ? "" : vacancyPostingModel.job?.minimumSalary}
                            min={0}
                            styles={styles.textInput}
                            placeHolder={"Min"}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.minimumSalary}                                      
                        />
                    </div>
                    <div style={{...styles.inputWrapper, ...styles.leftMargin}}>
                        <TextInput
                            inputType={"number"}
                            name={"maximumSalary"}
                            value={vacancyPostingModel.job?.maximumSalary === 0 ? "" : vacancyPostingModel.job?.maximumSalary}
                            min={0}
                            styles={styles.textInput}
                            placeHolder={"Max"}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.maximumSalary}                                       
                        />
                    </div>
                </div>
                <div 
                    style={styles.inputWrapper} 
                    onMouseEnter={() => setExpandItem("benefit")}
                    onMouseLeave={() => setExpandItem(undefined)}
                >
                    <label style={styles.label}>{"Benefits"}</label>
                    <div style={{...styles.benifitContainer, ...((expandItem === "benefit") && styles.benifitContainer.hover)}}>
                        {benefits.filter((benefit) => benefit.isSelected).map((benefit) =>
                            <div 
                                key={benefit.benefitId}
                                style={styles.selectorWrapper}
                                onMouseEnter={() => setHoveredBenefitId(benefit.benefitId)}
                                onMouseLeave={() => setHoveredBenefitId(0)}
                            >
                                {benefit.benefit}
                                {hoveredBenefitId === benefit.benefitId && <CloseCircle style={styles.closeIcon} onClick={() => toggleBenefit(benefit.benefitId)} />}
                            </div>
                        )}                                        
                    </div>
                    <div style={styles.customerWrapper}>
                        <input
                            type="text"
                            value={customBenefit}
                            onChange={(e) => setCustomBenefit(e.target.value)}
                            onKeyDown={handleCustomBenefitKeyDown}
                            placeholder={isMobile ? "Type custom benefit & Click plus Button" : "Type custom benefit & press Enter"}
                            style={styles.customBenifit}   
                        />
                        <Plus 
                            onClick={handleAddCustomBenefit}
                            style={styles.addButton}
                        />
                    </div>
                    {(expandItem === "benefit") && <div style={styles.benifitHoverWrapper}>
                        <div style={styles.levelScrollArea}>
                            {benefits.filter((benefit) => !benefit.isSelected).map((benefit, index) => 
                                <React.Fragment key={index}>
                                    <div 
                                        style={{ 
                                            ...styles.levelDropDownItem, 
                                            ...(hoveredIndex === index && styles.levelDropDownItem.hover) 
                                        }} 
                                        onClick={() => toggleBenefit(benefit.benefitId)} 
                                        onMouseEnter={() => setHoveredIndex(index)} 
                                        onMouseLeave={() => setHoveredIndex(-1)}
                                    >
                                        {benefit.benefit}
                                    </div>
                                    {index < benefits.filter((benefit) => !benefit.isSelected).length - 1 && 
                                    <hr 
                                        style={{ 
                                            ...styles.benifitbottomBorder, 
                                            ...((hoveredIndex === index) && styles.hoveredBorder) 
                                        }} 
                                    />}
                                </React.Fragment>
                            )}
                        </div>
                    </div>}
                </div>
                <div style={styles.textAreaInputWrapper}>
                    <label style={styles.label}>{"Overview"}</label>
                    <TextAreaInput                
                        name={"overview"}
                        value={vacancyPostingModel.job?.overview}                                                 
                        styles={styles.textAreaInput}
                        handleChange={handleTextAreaChange}                    
                    /> 
                </div>
                <div style={styles.inputWrapper}>
                    <div style={styles.labelWrapper}>
                        <label style={styles.label}>{"Job Description"}</label> 
                    </div>
                    <QuillEditor 
                        styles={styles.coverLetterEditor} 
                        value={vacancyPostingModel.job?.jobDescription} 
                        onChange={handleJobDescriptionChange}
                    />
                </div>
                <div style={styles.inputWrapper}> 
                    <label style={styles.label}>{"Description Image (Optional)"}</label>
                    <FileInput
                        name={'Attachments'}
                        selectedFileName={vacancyPostingModel.attachment ? vacancyPostingModel.attachment : "Choose a Image"}
                        styles={styles.attachment}
                        handleChange={(event) => handleFileChange(event!.currentTarget.files!)}
                    /> 
                    <div style={styles.instructionArea}>
                        <div style={styles.instruction}>
                            <span style={styles.textMedium}>{"Support files : "}</span>{" png, jpg, jpeg |"}<span style={styles.textMedium}>{" Max file size :"}</span>{" 2MB"}
                        </div>
                        {jobDescriptionImageUrl &&  
                            <div style={styles.imageText}> 
                                <span onClick={handleViewJobImage}>{"View Current Image"}</span>
                                <RedCloseCircle style={styles.redCloseIcon} onClick={() => handleRemoveJobImage()}/> 
                            </div> 
                        }
                    </div>
                </div>
            </form>
            <Alert />
            <PopUpWindow open={viewJobImage} styles={styles.uploadImagePopup} toggle={()=> setViewJobImage(!viewJobImage)} closeCircle={true}>
                <img style={styles.uploadedImage} src={jobDescriptionImageUrl!} alt="Job description image" />
            </PopUpWindow>
        </div>    
    )
})
